import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AreaMap } from "@/components/AreaMap";
import { CATEGORIES, STATUSES, CATEGORY_LIST, fmtMoney } from "@/lib/leadMeta";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, MapPinned, Hand, Navigation } from "lucide-react";

export default function AreaSearch() {
  const { user } = useAuth();
  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState(25);
  const [type, setType] = useState("all");
  const [result, setResult] = useState({ origin: null, leads: [] });
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = { radius };
      if (zip) params.zip = zip;
      if (type !== "all") params.insurance_type = type;
      const { data } = await api.get("/leads/search/area", { params });
      setResult(data);
      if (zip && !data.origin) toast.error("ZIP not recognized — showing all leads instead");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [zip, radius, type]);

  useEffect(() => { search(); /* initial load shows all */ }, []); // eslint-disable-line

  const claim = async (lead) => {
    try {
      await api.post(`/leads/${lead.lead_id}/claim`);
      toast.success(`Claimed ${lead.name}`);
      search();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const leads = result.leads || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Area lead finder</h1>
        <p className="mt-1 text-sm text-muted-foreground">Search leads by ZIP code and radius across your territory.</p>
      </div>

      <Card className="p-5">
        <div className="grid gap-5 md:grid-cols-[1fr_2fr_1fr_auto] md:items-end">
          <div>
            <Label>ZIP code</Label>
            <div className="relative mt-1.5">
              <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="e.g. 90012" className="pl-9" data-testid="area-zip-search-input" />
            </div>
          </div>
          <div>
            <Label>Radius: <span className="font-mono text-primary">{radius} mi</span></Label>
            <Slider value={[radius]} min={5} max={50} step={5} onValueChange={(v) => setRadius(v[0])} className="mt-4" data-testid="radius-slider-input" />
          </div>
          <div>
            <Label>Product</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1.5" data-testid="area-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {CATEGORY_LIST.map((k) => <SelectItem key={k} value={k}>{CATEGORIES[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={search} disabled={loading} data-testid="area-search-button"><Search className="mr-2 h-4 w-4" /> Search</Button>
        </div>
        <p className="mt-3 font-mono text-xs text-muted-foreground">
          Try: 90012, 10001, 60601, 77002, 33101, 98101, 30303, 80202
        </p>
      </Card>

      {result.suggested_owner && (
        <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm" data-testid="territory-suggestion">
          <MapPinned className="h-4 w-4 text-primary" />
          ZIP <span className="font-mono">{result.origin?.zip || zip}</span> is <span className="font-medium">{result.suggested_owner.name}</span>'s territory ({result.suggested_owner.role})
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <AreaMap leads={leads} origin={result.origin} radius={radius} />

        <Card className="flex max-h-[520px] flex-col overflow-hidden">
          <div className="border-b border-border p-4">
            <h3 className="font-heading font-semibold">{leads.length} lead{leads.length !== 1 ? "s" : ""} {result.origin ? "in range" : "total"}</h3>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="area-results-list">
            {leads.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">No leads found in this area.</p>
            ) : leads.map((l) => {
              const c = CATEGORIES[l.insurance_type] || CATEGORIES.life;
              const canClaim = user.role === "agent" && (!l.assigned_to || l.assigned_to === user.user_id);
              return (
                <div key={l.lead_id} className="flex items-center gap-3 border-b border-border/60 p-4" data-testid={`area-lead-${l.lead_id}`}>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: c.color + "1f" }}>
                    <c.icon className="h-4 w-4" style={{ color: c.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{l.name}</p>
                    <p className="text-xs text-muted-foreground">{l.city}, {l.state}
                      {l.distance_miles != null && <span className="ml-2 font-mono text-teal-400"><Navigation className="mr-0.5 inline h-3 w-3" />{l.distance_miles}mi</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-xs">{fmtMoney(l.premium_estimate)}</p>
                    <span className="text-[10px]" style={{ color: STATUSES[l.status]?.color }}>{STATUSES[l.status]?.label}</span>
                  </div>
                  {canClaim && <Button size="icon" variant="ghost" onClick={() => claim(l)} data-testid={`area-claim-${l.lead_id}`} title="Claim lead"><Hand className="h-4 w-4" /></Button>}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
